import { useCancelRequestHook } from '@op/hooks/useCancelRequest';
import { getMultiGamesComments, getMultiGamesMaxMetric } from '@opm/services';
import { useStableValues } from '@opm/utils/stableKey';
import { useState, useEffect, useMemo } from 'react';

interface IUseMultiGamesTrendingRequestParams {
  filter: any;
  games?: Array<{
    edition_unified_id: string;
    entity_type: string;
    id_type: string;
  }>;
  sortBy?: any;
  extraFilter?: any;
  requestMax?: boolean;
}

/**
 * 多游戏版本的 useTrendingRequest
 */
export function useMultiGamesTrendingRequest({
  filter,
  sortBy,
  extraFilter,
  requestMax = true,
}: IUseMultiGamesTrendingRequestParams) {
  const [data, setData] = useState<any>({ list: [], statistics: {}, total: 0 });
  const [error, setError] = useState(false);

  const { wrapperRequestTile, loading: maxLoading } = useCancelRequestHook();
  const {
    wrapperRequestTile: wrapperRequestTileComments,
    loading: commentsLoading,
  } = useCancelRequestHook({
    clear: () => setData({ list: [], statistics: {}, total: 0 }),
  });

  const [stableFilter, stableSortBy] = useStableValues(filter, sortBy);

  function fetchData(noLoading = false) {
    if (!filter) return;

    let sort_item = 'comment_time';
    let sort_model = 'desc';
    if (sortBy) {
      sort_item = sortBy.by || sort_item;
      sort_model = sortBy.order || sort_model;
    }

    const baseParams = {
      ...filter,
      page: 1,
      page_size: 50,
      sort_item,
      sort_model,
      ...(extraFilter && Object.keys(extraFilter).length > 0
        ? { ...extraFilter }
        : {}),
    };

    const requestList = [
      wrapperRequestTileComments(getMultiGamesComments, baseParams, noLoading),
    ];

    if (requestMax) {
      requestList.push(
        wrapperRequestTile(
          getMultiGamesMaxMetric,
          { ...filter, ...(extraFilter || {}) },
          noLoading,
        ),
      );
    }

    Promise.all(requestList)
      .then(([tableList, totalInfo = []]) => {
        if (tableList) {
          const list = Array.isArray(tableList.list) ? tableList.list : [];
          setData({
            list,
            statistics: totalInfo.length > 0 ? totalInfo[0] : {},
            total: list.length,
          });
          setError(false);
        } else {
          setData({ list: [], statistics: {}, total: 0 });
          setError(true);
        }
      })
      .catch(() => {
        setError(true);
      });
  }

  const loading = useMemo(() => {
    return maxLoading || commentsLoading;
  }, [maxLoading, commentsLoading]);

  useEffect(() => {
    if (stableFilter) {
      fetchData();
    }
  }, [stableFilter, stableSortBy]);

  return { data, loading, error, refetch: fetchData };
}
